const EXPERTS_ALIVE_API_BASE_URL = 'https://pa4mo8ntn0.execute-api.us-east-2.amazonaws.com';
const INPUT_SAMPLE_RATE = 24_000;
const SPEAKER_TAIL_MS = 650;
const AUDIBLE_SAMPLE_THRESHOLD = 0.0025;
const MOTION_RELEASE_MS = 80;
function bytesToBase64(bytes) {
    let binary = '';
    for (let index = 0; index < bytes.length; index += 1)
        binary += String.fromCharCode(bytes[index]);
    return btoa(binary);
}
function base64ToBytes(value) {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1)
        bytes[index] = binary.charCodeAt(index);
    return bytes;
}
function float32ToPcm16(input, inputRate) {
    const ratio = inputRate / INPUT_SAMPLE_RATE;
    const outputLength = Math.max(1, Math.floor(input.length / ratio));
    const output = new Int16Array(outputLength);
    for (let index = 0; index < outputLength; index += 1) {
        const position = index * ratio;
        const lower = Math.floor(position);
        const upper = Math.min(input.length - 1, lower + 1);
        const fraction = position - lower;
        const sample = Math.max(-1, Math.min(1, input[lower] + (input[upper] - input[lower]) * fraction));
        output[index] =
            sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff);
    }
    return new Uint8Array(output.buffer);
}
function pcm16ToFloat32(bytes) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const output = new Float32Array(Math.floor(bytes.byteLength / 2));
    for (let index = 0; index < output.length; index += 1) {
        const sample = view.getInt16(index * 2, true);
        output[index] = sample < 0 ? sample / 0x8000 : sample / 0x7fff;
    }
    return output;
}
export function findAudibleSampleRange(samples, threshold = AUDIBLE_SAMPLE_THRESHOLD) {
    let first = -1;
    let last = -1;
    for (let index = 0; index < samples.length; index += 1) {
        if (Math.abs(samples[index]) < threshold)
            continue;
        if (first < 0)
            first = index;
        last = index;
    }
    return first < 0 ? null : { first, last };
}
async function authorize(options) {
    const sessionEndpoint = options.sessionEndpoint ?? `${EXPERTS_ALIVE_API_BASE_URL}/sdk/session`;
    const headers = { 'Content-Type': 'application/json' };
    if (options.deploymentKey)
        headers.Authorization = `Bearer ${options.deploymentKey}`;
    const response = await fetch(sessionEndpoint, {
        body: JSON.stringify({
            group: options.group,
            humanId: options.humanId,
            language: options.language,
            platform: options.platform,
            platformId: options.platformId ?? window.location.origin,
            resolution: options.resolution,
            tutorName: options.tutorName,
            voice: options.voice,
        }),
        headers,
        method: 'POST',
    });
    const payload = (await response.json().catch(() => ({})));
    if (!response.ok)
        throw new Error(payload.message ?? 'Experts Alive could not start this session.');
    return payload;
}
/**
 * Native 24 kHz PCM transport for Firefox. It avoids Firefox's unstable
 * long-lived Realtime WebRTC audio route while preserving the SDK API.
 */
export async function connectSdkRealtimeWebSocket(options) {
    options.onState('connecting');
    const AudioContextConstructor = window.AudioContext ??
        window
            .webkitAudioContext;
    if (!AudioContextConstructor)
        throw new Error('Web Audio is not available in this browser.');
    const audioContext = new AudioContextConstructor();
    const ensureAudioContextRunning = async () => {
        if (audioContext.state === 'suspended')
            await audioContext.resume();
        if (audioContext.state !== 'running')
            throw new Error('Firefox could not start speaker playback. Select Start conversation again.');
    };
    await ensureAudioContextRunning();
    const session = await authorize(options);
    const ephemeralKey = typeof session.client_secret === 'string'
        ? session.client_secret
        : (session.client_secret?.value ?? session.value);
    if (!ephemeralKey) {
        void audioContext.close();
        throw new Error('The Experts Alive runtime did not return a session key.');
    }
    const model = session.model ?? 'gpt-realtime-2.1-mini';
    const microphoneEnabled = options.microphone !== false;
    const inputStream = microphoneEnabled
        ? await navigator.mediaDevices.getUserMedia({
            audio: {
                autoGainControl: true,
                echoCancellation: true,
                noiseSuppression: true,
            },
            video: false,
        })
        : new MediaStream();
    await ensureAudioContextRunning();
    const inputSource = microphoneEnabled
        ? audioContext.createMediaStreamSource(inputStream)
        : null;
    const inputProcessor = microphoneEnabled
        ? audioContext.createScriptProcessor(2048, 1, 1)
        : null;
    const silentGain = microphoneEnabled ? audioContext.createGain() : null;
    if (inputSource && inputProcessor && silentGain) {
        silentGain.gain.value = 0;
        inputSource.connect(inputProcessor);
        inputProcessor.connect(silentGain);
        silentGain.connect(audioContext.destination);
    }
    const outputGain = audioContext.createGain();
    const motionDestination = audioContext.createMediaStreamDestination();
    outputGain.gain.value = 1;
    outputGain.connect(audioContext.destination);
    outputGain.connect(motionDestination);
    const detachMotion = options.motionRuntime?.attachOutputMediaStream(motionDestination.stream) ??
        null;
    options.onOutputStream?.(motionDestination.stream);
    const socket = new WebSocket(`wss://api.openai.com/v1/realtime?model=${encodeURIComponent(model)}`, [
        'realtime',
        `openai-insecure-api-key.${ephemeralKey}`,
        'openai-agents-sdk.experts-alive',
    ]);
    let disconnected = false;
    let ready = false;
    let listening = false;
    let responseInProgress = false;
    let playbackActive = false;
    let outputDone = false;
    let assistantText = '';
    let listeningTimer = 0;
    let readyTimer = 0;
    let nextPlaybackTime = 0;
    let playbackGeneration = 0;
    let motionActive = false;
    let motionStartTimer = 0;
    let motionStopTimer = 0;
    let lastAudiblePlaybackTime = 0;
    let audioScheduleQueue = Promise.resolve();
    const scheduledSources = new Set();
    const send = (payload) => {
        if (socket.readyState === WebSocket.OPEN)
            socket.send(JSON.stringify(payload));
    };
    const setListening = (enabled) => {
        listening =
            enabled &&
                microphoneEnabled &&
                ready &&
                !disconnected &&
                !responseInProgress &&
                !playbackActive;
    };
    const clearListeningTimer = () => {
        if (listeningTimer)
            window.clearTimeout(listeningTimer);
        listeningTimer = 0;
    };
    const startListening = (delayMs = 0) => {
        clearListeningTimer();
        setListening(false);
        if (!microphoneEnabled || !ready || disconnected)
            return false;
        listeningTimer = window.setTimeout(() => {
            listeningTimer = 0;
            if (responseInProgress ||
                playbackActive ||
                socket.readyState !== WebSocket.OPEN)
                return;
            send({ type: 'input_audio_buffer.clear' });
            setListening(true);
            options.onState('listening');
        }, delayMs);
        return true;
    };
    const clearMotionTimers = () => {
        if (motionStartTimer)
            window.clearTimeout(motionStartTimer);
        if (motionStopTimer)
            window.clearTimeout(motionStopTimer);
        motionStartTimer = 0;
        motionStopTimer = 0;
    };
    const stopMotion = (reason) => {
        clearMotionTimers();
        if (!motionActive)
            return;
        motionActive = false;
        options.motionRuntime?.returnToIdle(reason);
    };
    const beginMotionAt = (audioTime) => {
        if (motionActive || motionStartTimer)
            return;
        const generation = playbackGeneration;
        const delayMs = Math.max(0, Math.round((audioTime - audioContext.currentTime) * 1_000));
        motionStartTimer = window.setTimeout(() => {
            motionStartTimer = 0;
            if (generation !== playbackGeneration || disconnected || !playbackActive)
                return;
            motionActive = true;
            options.motionRuntime?.beginSpeaking({
                holdThroughSilence: true,
                source: 'realtime-websocket-pcm',
            });
            options.onState('speaking');
        }, delayMs);
    };
    const scheduleMotionStop = () => {
        if (!outputDone || !lastAudiblePlaybackTime)
            return;
        if (motionStopTimer)
            window.clearTimeout(motionStopTimer);
        const generation = playbackGeneration;
        const delayMs = Math.max(0, Math.round((lastAudiblePlaybackTime - audioContext.currentTime) * 1_000) + MOTION_RELEASE_MS);
        motionStopTimer = window.setTimeout(() => {
            motionStopTimer = 0;
            if (generation !== playbackGeneration || !outputDone)
                return;
            if (motionActive) {
                motionActive = false;
                options.motionRuntime?.returnToIdle('realtime-websocket-pcm-audible-end');
            }
        }, delayMs);
    };
    const finishPlayback = () => {
        if (!playbackActive || scheduledSources.size)
            return;
        playbackActive = false;
        outputDone = false;
        assistantText = '';
        lastAudiblePlaybackTime = 0;
        options.motionRuntime?.updateSpeechGuide('');
        stopMotion('realtime-websocket-pcm-drained');
        options.onState('ready');
        if (options.autoListen !== false)
            startListening(SPEAKER_TAIL_MS);
    };
    const stopPlayback = () => {
        playbackGeneration += 1;
        clearMotionTimers();
        for (const source of scheduledSources) {
            source.onended = null;
            try {
                source.stop();
            }
            catch {
                // Already stopped.
            }
        }
        scheduledSources.clear();
        nextPlaybackTime = audioContext.currentTime;
        playbackActive = false;
        outputDone = false;
        lastAudiblePlaybackTime = 0;
        stopMotion('realtime-websocket-pcm-interrupted');
    };
    const scheduleAudioChunk = async (base64Audio) => {
        await ensureAudioContextRunning();
        const samples = pcm16ToFloat32(base64ToBytes(base64Audio));
        if (!samples.length || disconnected)
            return;
        const buffer = audioContext.createBuffer(1, samples.length, INPUT_SAMPLE_RATE);
        buffer.copyToChannel(new Float32Array(samples), 0);
        const source = audioContext.createBufferSource();
        const generation = playbackGeneration;
        source.buffer = buffer;
        source.connect(outputGain);
        const startsAt = Math.max(audioContext.currentTime + 0.025, nextPlaybackTime);
        nextPlaybackTime = startsAt + buffer.duration;
        if (!playbackActive) {
            playbackActive = true;
            outputDone = false;
            lastAudiblePlaybackTime = 0;
            clearListeningTimer();
            setListening(false);
        }
        const audibleRange = findAudibleSampleRange(samples);
        if (audibleRange) {
            beginMotionAt(startsAt + audibleRange.first / INPUT_SAMPLE_RATE);
            lastAudiblePlaybackTime = Math.max(lastAudiblePlaybackTime, startsAt + (audibleRange.last + 1) / INPUT_SAMPLE_RATE);
        }
        scheduledSources.add(source);
        source.onended = () => {
            scheduledSources.delete(source);
            source.disconnect();
            if (generation === playbackGeneration && outputDone)
                finishPlayback();
        };
        source.start(startsAt);
    };
    const scheduleAudio = (base64Audio) => {
        audioScheduleQueue = audioScheduleQueue
            .then(() => scheduleAudioChunk(base64Audio))
            .catch((cause) => {
            setListening(false);
            stopPlayback();
            options.onState('error');
            options.onError(cause instanceof Error
                ? cause.message
                : 'Firefox could not start speaker playback.');
        });
    };
    if (inputProcessor) {
        inputProcessor.onaudioprocess = (event) => {
            if (!listening || socket.readyState !== WebSocket.OPEN)
                return;
            const samples = event.inputBuffer.getChannelData(0);
            const pcm = float32ToPcm16(samples, audioContext.sampleRate);
            send({
                audio: bytesToBase64(pcm),
                type: 'input_audio_buffer.append',
            });
        };
    }
    let resolveReady = null;
    let rejectReady = null;
    const readyPromise = new Promise((resolve, reject) => {
        resolveReady = resolve;
        rejectReady = reject;
    });
    const interruptTransport = () => {
        if (disconnected)
            return;
        setListening(false);
        stopPlayback();
        options.onState('error');
        options.onTransportInterrupted?.('peer-disconnected');
        if (!options.onTransportInterrupted)
            options.onError('Audio connection interrupted. Reconnect to continue.');
    };
    socket.addEventListener('open', () => {
        send({
            session: {
                type: 'realtime',
                instructions: options.conversationInstructions,
                audio: {
                    input: {
                        format: { rate: INPUT_SAMPLE_RATE, type: 'audio/pcm' },
                        noise_reduction: { type: 'near_field' },
                        turn_detection: {
                            create_response: false,
                            interrupt_response: false,
                            prefix_padding_ms: 300,
                            silence_duration_ms: 650,
                            threshold: 0.9,
                            type: 'server_vad',
                        },
                    },
                    output: {
                        format: { rate: INPUT_SAMPLE_RATE, type: 'audio/pcm' },
                    },
                },
            },
            type: 'session.update',
        });
        readyTimer = window.setTimeout(() => {
            readyTimer = 0;
            if (ready || disconnected)
                return;
            rejectReady?.(new Error('The safe microphone policy could not be confirmed. Reconnect to continue.'));
            rejectReady = null;
        }, 5_000);
    });
    socket.addEventListener('message', (message) => {
        try {
            const event = JSON.parse(String(message.data));
            const type = event.type ?? '';
            if (type === 'session.updated') {
                if (readyTimer)
                    window.clearTimeout(readyTimer);
                readyTimer = 0;
                ready = true;
                options.onState('ready');
                if (options.greetingPrompt) {
                    responseInProgress = true;
                    send({
                        response: { instructions: options.greetingPrompt },
                        type: 'response.create',
                    });
                }
                else if (options.autoListen !== false)
                    startListening();
                resolveReady?.();
                resolveReady = null;
                rejectReady = null;
            }
            if (type === 'response.created') {
                responseInProgress = true;
                setListening(false);
                assistantText = '';
                options.motionRuntime?.updateSpeechGuide('');
                options.onAssistantText('');
            }
            if (type.includes('input_audio_buffer.speech_started') &&
                listening &&
                !playbackActive)
                options.onState('listening');
            if (type.includes('input_audio_buffer.committed') &&
                listening &&
                !responseInProgress &&
                !playbackActive) {
                setListening(false);
                responseInProgress = true;
                send({ type: 'response.create' });
            }
            if (type.includes('conversation.item.input_audio_transcription.completed') &&
                event.transcript)
                options.onUserText(event.transcript.trim());
            if ((type.includes('response.output_audio_transcript.delta') ||
                type.includes('response.audio_transcript.delta')) &&
                event.delta) {
                assistantText += event.delta;
                options.motionRuntime?.updateSpeechGuide(assistantText);
                options.onAssistantText(assistantText);
            }
            if ((type.includes('response.output_audio_transcript.done') ||
                type.includes('response.audio_transcript.done')) &&
                event.transcript) {
                assistantText = event.transcript.trim();
                options.motionRuntime?.updateSpeechGuide(assistantText);
                options.onAssistantText(assistantText);
            }
            if (type === 'response.output_audio.delta' && event.delta)
                scheduleAudio(event.delta);
            if (type === 'response.done') {
                responseInProgress = false;
                void audioScheduleQueue.then(() => {
                    if (disconnected)
                        return;
                    outputDone = true;
                    scheduleMotionStop();
                    finishPlayback();
                    if (!playbackActive && options.autoListen !== false)
                        startListening(SPEAKER_TAIL_MS);
                });
            }
            if (event.error?.message) {
                setListening(false);
                options.onState('error');
                options.onError(event.error.message);
            }
        }
        catch {
            // Ignore lifecycle messages that do not affect the SDK controller.
        }
    });
    socket.addEventListener('error', () => {
        const cause = new Error('Firefox could not open the stable audio connection.');
        if (!ready)
            rejectReady?.(cause);
        else
            interruptTransport();
    });
    socket.addEventListener('close', () => {
        if (!ready)
            rejectReady?.(new Error('Firefox could not open the stable audio connection.'));
        else
            interruptTransport();
    });
    try {
        await readyPromise;
    }
    catch (cause) {
        disconnected = true;
        if (readyTimer)
            window.clearTimeout(readyTimer);
        if (inputProcessor)
            inputProcessor.onaudioprocess = null;
        inputStream.getTracks().forEach((track) => track.stop());
        inputSource?.disconnect();
        inputProcessor?.disconnect();
        silentGain?.disconnect();
        outputGain.disconnect();
        motionDestination.disconnect();
        detachMotion?.();
        socket.close();
        void audioContext.close();
        throw cause;
    }
    return {
        session: {
            humanId: session.humanId,
            resolution: session.resolution,
            watermark: session.watermark,
        },
        beginListening: () => startListening(),
        cancelResponse: () => {
            setListening(false);
            if (responseInProgress)
                send({ type: 'response.cancel' });
            responseInProgress = false;
            stopPlayback();
            if (options.autoListen !== false)
                startListening(SPEAKER_TAIL_MS);
        },
        disconnect: () => {
            if (disconnected)
                return;
            disconnected = true;
            ready = false;
            clearListeningTimer();
            if (readyTimer)
                window.clearTimeout(readyTimer);
            readyTimer = 0;
            setListening(false);
            stopPlayback();
            if (inputProcessor)
                inputProcessor.onaudioprocess = null;
            inputStream.getTracks().forEach((track) => track.stop());
            inputSource?.disconnect();
            inputProcessor?.disconnect();
            silentGain?.disconnect();
            outputGain.disconnect();
            motionDestination.disconnect();
            detachMotion?.();
            options.motionRuntime?.updateSpeechGuide('');
            options.motionRuntime?.returnToIdle('realtime-websocket-disconnected');
            options.onOutputStream?.(null);
            socket.close(1000, 'session-ended');
            void audioContext.close();
            options.onState('ready');
        },
        sendText: (text) => {
            const value = text.trim();
            if (!value ||
                disconnected ||
                !ready ||
                responseInProgress ||
                playbackActive ||
                socket.readyState !== WebSocket.OPEN)
                return false;
            clearListeningTimer();
            setListening(false);
            responseInProgress = true;
            send({
                item: {
                    content: [{ text: value, type: 'input_text' }],
                    role: 'user',
                    type: 'message',
                },
                type: 'conversation.item.create',
            });
            send({ type: 'response.create' });
            options.onUserText(value);
            return true;
        },
    };
}
